import { useState } from 'react';

import { cn } from '@/lib/cn';

import { changeSentence, humanChange, type HumanChange } from './storeChanges';

/*
 * Field changes as a reviewer reads them, for the History panel, the
 * change waiting for review and "edited since approval":
 *
 *   - an FAQ edit lists the questions added, removed or reworded (matched
 *     by question, so removing one does not list every later item as
 *     changed), not the whole FAQ twice;
 *   - the store headline and the hero headline, which one owner edit
 *     changes together, read as one line (same for the subheadline, and
 *     the accent and primary colour, "Primary color");
 *   - photos are before/after thumbnails; a replaced file that is gone
 *     reads "Removed";
 *   - a list spanning several saves (edited since approval, changed since
 *     a request, what a revert undoes) shows each field's net change once.
 */

export interface FieldChange {
  field: string;
  old: unknown;
  new: unknown;
}

interface FaqItem {
  q: string;
  a: string;
}

function text(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** The FAQ as the backend writes it in a change ("Q: …\nA: …\nQ: …"), or
 *  an items array. */
export function parseFaq(value: unknown): FaqItem[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item ?? {}) as Record<string, unknown>)
      .map((item) => ({ q: String(item.q ?? '').trim(), a: String(item.a ?? '').trim() }))
      .filter((item) => item.q || item.a);
  }
  const raw = text(value);
  if (!raw) return [];
  const items: FaqItem[] = [];
  let current: FaqItem | null = null;
  for (const line of raw.split('\n')) {
    const q = /^\s*Q:\s?(.*)$/.exec(line);
    const a = /^\s*A:\s?(.*)$/.exec(line);
    if (q) {
      current = { q: q[1].trim(), a: '' };
      items.push(current);
    } else if (a && current) {
      current.a = a[1].trim();
    } else if (current) {
      // A multi-line answer (or question) continues.
      if (current.a) current.a += `\n${line}`;
      else current.q += `\n${line}`;
    }
  }
  return items;
}

export type FaqChange =
  | { kind: 'added'; index: number; item: FaqItem }
  | { kind: 'removed'; index: number; item: FaqItem }
  | { kind: 'question'; index: number; from: string; to: string }
  | { kind: 'answer'; index: number; question: string; from: string; to: string }
  | { kind: 'order' };

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/** What changed in an FAQ, item by item: matched by question first, then
 *  by position for a reworded question. */
export function faqChanges(before: FaqItem[], after: FaqItem[]): FaqChange[] {
  const out: FaqChange[] = [];
  const oldByQ = new Map(before.map((item, i) => [norm(item.q), i]));
  const newByQ = new Map(after.map((item, i) => [norm(item.q), i]));
  const unmatchedOld = before.map((_, i) => i).filter((i) => !newByQ.has(norm(before[i].q)));
  const unmatchedNew = after.map((_, i) => i).filter((i) => !oldByQ.has(norm(after[i].q)));
  // A reworded question at the same position is an edit, not remove + add.
  const reworded = new Set<number>();
  for (const i of unmatchedNew) {
    if (unmatchedOld.includes(i)) {
      reworded.add(i);
      out.push({ kind: 'question', index: i, from: before[i].q, to: after[i].q });
      if (norm(before[i].a) !== norm(after[i].a)) {
        out.push({ kind: 'answer', index: i, question: after[i].q, from: before[i].a, to: after[i].a });
      }
    }
  }
  for (const i of unmatchedOld) {
    if (!reworded.has(i)) out.push({ kind: 'removed', index: i, item: before[i] });
  }
  for (const i of unmatchedNew) {
    if (!reworded.has(i)) out.push({ kind: 'added', index: i, item: after[i] });
  }
  after.forEach((item, i) => {
    const j = oldByQ.get(norm(item.q));
    if (j !== undefined && norm(before[j].a) !== norm(item.a)) {
      out.push({ kind: 'answer', index: i, question: item.q, from: before[j].a, to: item.a });
    }
  });
  const sharedOld = before.map((item) => norm(item.q)).filter((q) => newByQ.has(q));
  const sharedNew = after.map((item) => norm(item.q)).filter((q) => oldByQ.has(q));
  if (sharedOld.join('|') !== sharedNew.join('|')) out.push({ kind: 'order' });
  return out;
}

function isFaqField(field: string): boolean {
  return /^design\.[a-z_]+\.items$/.test(field);
}

const clip = (s: string, n = 90) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** One line for an FAQ change (the "changed since you opened it" notice). */
export function faqSentence(change: FieldChange): string {
  const list = faqChanges(parseFaq(change.old), parseFaq(change.new));
  if (list.length === 0) return 'FAQ updated';
  const parts = list.slice(0, 3).map((c) => {
    switch (c.kind) {
      case 'added':
        return `question added (“${clip(c.item.q, 60)}”)`;
      case 'removed':
        return `question removed (“${clip(c.item.q, 60)}”)`;
      case 'question':
        return `question ${c.index + 1} reworded`;
      case 'answer':
        return `answer to “${clip(c.question, 50)}” changed`;
      case 'order':
        return 'questions reordered';
    }
  });
  return `FAQ: ${parts.join('; ')}${list.length > 3 ? `; and ${list.length - 3} more` : ''}`;
}

/** Fields one owner edit changes together, and the label they read as
 *  when both moved to the same value: the store and hero headline (and
 *  subheadline), and the accent and the template's primary colour. */
const PAIRED_FIELDS: Record<string, { pair: string; label: string }> = {
  headline: { pair: 'design.hero.headline', label: 'Headline (store and hero)' },
  'design.hero.headline': { pair: 'headline', label: 'Headline (store and hero)' },
  subheadline: { pair: 'design.hero.subheadline', label: 'Subheadline (store and hero)' },
  'design.hero.subheadline': { pair: 'subheadline', label: 'Subheadline (store and hero)' },
  accent_color: { pair: 'design.theme.primary', label: 'Primary color' },
  'design.theme.primary': { pair: 'accent_color', label: 'Primary color' },
};

const same = (a: unknown, b: unknown) =>
  (text(a) ?? '').trim().toLowerCase() === (text(b) ?? '').trim().toLowerCase();

const isColorField = (field: string) => field === 'accent_color' || field.startsWith('design.theme.');

/** Colours compare as hex, any case; everything else exactly. */
const equal = (field: string, a: unknown, b: unknown) =>
  isColorField(field) ? same(a, b) : text(a) === text(b);

/** Each field once, from its first old value to its last new value (the
 *  changes oldest first). A field that ended where it started drops out,
 *  and a photo replaced twice reads as one before/after instead of
 *  "Removed → Removed". */
export function netChanges(changes: FieldChange[]): FieldChange[] {
  const byField = new Map<string, FieldChange>();
  for (const change of changes) {
    const first = byField.get(change.field);
    // Re-inserted so the field sits where it last changed.
    byField.delete(change.field);
    byField.set(change.field, first ? { ...change, old: first.old } : change);
  }
  return [...byField.values()].filter((change) => !equal(change.field, change.old, change.new));
}

/** The changes of one save, with fields one edit changes together (the
 *  store and hero headline, the accent and primary colour) merged when they
 *  changed to the same value. */
export function mergeChanges(changes: FieldChange[]): (FieldChange & { label?: string })[] {
  const out: (FieldChange & { label?: string })[] = [];
  const skip = new Set<number>();
  changes.forEach((change, i) => {
    if (skip.has(i)) return;
    const paired = PAIRED_FIELDS[change.field];
    const j = paired
      ? changes.findIndex(
          (other, k) =>
            k !== i &&
            !skip.has(k) &&
            other.field === paired.pair &&
            equal(change.field, other.old, change.old) &&
            equal(change.field, other.new, change.new),
        )
      : -1;
    if (j >= 0 && paired) {
      skip.add(j);
      out.push({ ...change, label: paired.label });
      return;
    }
    out.push(change);
  });
  return out;
}

/** A photo in a diff; a replaced upload is deleted from storage, so a file
 *  that no longer loads reads "Removed" instead of a broken image. */
export function DiffThumb({ src, label, faded = false }: { src: string; label: string; faded?: boolean }) {
  const [gone, setGone] = useState(false);
  if (gone) {
    return (
      <span
        title={`${label} (file removed)`}
        className="w-48 h-48 border border-dashed border-line flex items-center justify-center type-label-xs text-ink-muted"
      >
        Removed
      </span>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" title={label}>
      <img
        src={src}
        alt={label}
        loading="lazy"
        onError={() => setGone(true)}
        className={cn('w-48 h-48 object-cover border border-line-subtle', faded && 'opacity-60')}
      />
    </a>
  );
}

function FaqDiff({ change }: { change: FieldChange }) {
  const list = faqChanges(parseFaq(change.old), parseFaq(change.new));
  if (list.length === 0) return <span className="text-ink-secondary">FAQ updated (no visible change)</span>;
  return (
    <span className="flex flex-col gap-4">
      <span className="text-ink">FAQ</span>
      <ul className="flex flex-col gap-4 pl-10 border-l-2 border-line-subtle">
        {list.map((c, i) => (
          <li key={i} className="text-ink-secondary break-words">
            {c.kind === 'added' ? (
              <>
                <span className="text-ink">Added:</span> “{c.item.q}”
                {c.item.a && <span className="block text-ink-muted">{clip(c.item.a, 200)}</span>}
              </>
            ) : c.kind === 'removed' ? (
              <>
                <span className="text-ink">Removed:</span>{' '}
                <span className="line-through decoration-ink-muted/60">“{c.item.q}”</span>
              </>
            ) : c.kind === 'question' ? (
              <>
                <span className="text-ink">Question {c.index + 1}:</span>{' '}
                <span className="line-through decoration-ink-muted/60">{c.from}</span> → {c.to}
              </>
            ) : c.kind === 'answer' ? (
              <>
                <span className="text-ink">Answer to “{clip(c.question, 70)}”:</span>{' '}
                <span className="line-through decoration-ink-muted/60">{clip(c.from, 220)}</span> →{' '}
                {clip(c.to, 220)}
              </>
            ) : (
              <span className="text-ink">Questions reordered</span>
            )}
          </li>
        ))}
      </ul>
    </span>
  );
}

function asEvent(change: FieldChange) {
  return { field: change.field, old_value: text(change.old), new_value: text(change.new) };
}

/** One change, readable: label, old → new, photos as thumbnails, the FAQ
 *  item by item. */
export function ChangeLine({
  change,
  label,
  productNames = {},
}: {
  change: FieldChange;
  label?: string;
  productNames?: Record<string, string>;
}) {
  if (isFaqField(change.field)) return <FaqDiff change={change} />;
  const human: HumanChange = humanChange(asEvent(change), productNames);
  const name = label ?? human.label;
  if (human.fromImage || human.toImage) {
    return (
      <span className="flex flex-col gap-4">
        <span className="text-ink-secondary">{changeSentence({ ...human, label: name })}</span>
        <span className="flex items-center gap-6">
          {human.fromImage && <DiffThumb src={human.fromImage} label={`${name} before`} faded />}
          {human.fromImage && human.toImage && (
            <span aria-hidden className="text-ink-muted">
              →
            </span>
          )}
          {human.toImage && <DiffThumb src={human.toImage} label={`${name} after`} />}
        </span>
      </span>
    );
  }
  return (
    <span className="text-ink-secondary break-words">
      <span className="text-ink">{name}</span>
      {human.from && human.to ? (
        <>
          : <span className="line-through decoration-ink-muted/60">{human.from}</span> → {human.to}
        </>
      ) : human.to ? (
        <>: set to {human.to}</>
      ) : (
        <> cleared</>
      )}
    </span>
  );
}

/** A list of changes (one save, a pending change, everything since the
 *  last approval). */
export function ChangeList({
  changes,
  productNames,
  className,
  net = false,
}: {
  changes: FieldChange[];
  productNames?: Record<string, string>;
  className?: string;
  /** Changes from several saves, oldest first: show each field's net
   *  change once (`netChanges`). */
  net?: boolean;
}) {
  const merged = mergeChanges(net ? netChanges(changes) : changes);
  return (
    <ul className={cn('flex flex-col gap-6 type-body-sm leading-130', className)}>
      {merged.map((change, i) => (
        <li key={`${change.field}-${i}`}>
          <ChangeLine change={change} label={change.label} productNames={productNames} />
        </li>
      ))}
    </ul>
  );
}
